import styled from '@emotion/styled';
import {Checkbox, FormControlLabel} from '@mui/material';
import React, {FocusEventHandler, useMemo} from 'react';

import {mixinFocus} from '@ELib/styles/mixins';

import {classy} from '../../utils';

import {FormInputWrapperLabel} from './FormInputWrapper';

export type ECheckboxProps = {
  id?: string;
  value?: boolean;
  fullWidth?: boolean;
  className?: string;
  onChange?: (newVal: boolean) => void;
  onFocus?: FocusEventHandler<HTMLInputElement> | undefined;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  label?: string | React.ReactElement;
  labelInfo?: string;
  sparseGutters?: boolean;
  disabled?: boolean;
  testId?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  stopPropagation?: boolean;
};

export const ECheckbox = ({
  id,
  value,
  onBlur,
  onChange,
  onFocus,
  label,
  labelInfo,
  fullWidth,
  className,
  sparseGutters,
  disabled,
  testId,
  onKeyDown,
  stopPropagation,
}: ECheckboxProps): React.ReactElement => {
  const checkBoxElement = useMemo(() => {
    // this variable only exists because if 'data-testid' is used directly inside "inputProps"
    // we run into a parse error by rollup.
    const testIdPropName = 'data-testid';
    const inputProps = {
      [testIdPropName]: testId ? `ECheckbox-${testId}` : undefined,
      onBlur,
      onFocus,
    } as React.InputHTMLAttributes<HTMLInputElement>;
    return (
      <StyledCheckbox
        checked={value}
        disabled={disabled}
        id={id}
        inputProps={inputProps}
        disableRipple
        onChange={() => onChange?.(!value)}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
        }}
        onKeyDown={onKeyDown}
      />
    );
  }, [
    testId,
    onBlur,
    onFocus,
    value,
    disabled,
    id,
    onKeyDown,
    onChange,
    stopPropagation,
  ]);

  return (
    <StyledFormControl
      className={classy({
        'sparse-gutters': Boolean(sparseGutters),
        [className || '']: Boolean(className),
      })}
      control={checkBoxElement}
      fullWidth={fullWidth}
      label={
        label || labelInfo ? (
          <FormInputWrapperLabel label={label ?? ''} labelInfo={labelInfo} />
        ) : undefined
      }
    />
  );
};

const StyledFormControl = styled(FormControlLabel, {
  shouldForwardProp: (props) => props !== 'fullWidth',
})<{
  fullWidth?: boolean;
}>`
  .MuiCheckbox-root {
    padding-top: 0;
    padding-bottom: 0;
  }

  .MuiTypography-root {
    font-size: inherit;
    font-weight: inherit;
  }

  &.sparse-gutters {
    margin-right: 0px;
    .MuiCheckbox-root {
      padding: 0 var(--gutter-tiny);
    }
  }

  width: ${({fullWidth}) => (fullWidth ? '100%' : 'fit-content')};
  & .MuiFormControlLabel-label {
    font-weight: var(--font-weight-thin);
    color: var(--color-core-black);
  }

  && .Mui-checked:not(.Mui-disabled) {
    color: var(--checkbox-checked-color);
  }
`;

const StyledCheckbox = styled(Checkbox)<{
  fullWidth?: boolean;
}>`
  :focus-within {
    svg {
      ${mixinFocus}
    }
  }
`;
